import { useSelector } from 'react-redux';

import { AuthzContext } from '../support/slices/userSlice';
import { RootState } from '../support/store';

/**
 * Client-side mirror of `db.permissions.salesforce_session_available`.
 *
 * Two signals, because a user can arrive at the toolbox two ways:
 *
 *   - signing in through Salesforce leaves the token in `state.user.sfToken` (and in the
 *     branch-prefixed `sf_token` cookie the token is hydrated from);
 *   - signing in through Cisco SSO and then linking Salesforce re-mints the Toolbox JWT, and
 *     the authz context built from it reports `salesforce.available`.
 *
 * The second is the one the backend can verify, so it is what the server gate trusts; the token
 * is checked too so the nav does not flicker off in the window before the authz context lands.
 * `available` alone is authoritative — `accessToken` is only populated for internal callers.
 */
export const hasSalesforceSession = (
  sfToken: string | null | undefined,
  authz: AuthzContext | null | undefined
): boolean => {
  if (sfToken) return true;

  const salesforce = authz?.salesforce;

  if (!salesforce) return false;

  return Boolean(
    salesforce.available ||
      salesforce.accessToken ||
      salesforce.access_token ||
      salesforce.idUrl ||
      salesforce.id_url
  );
};

/** Does the signed-in user currently have a Salesforce session? Gates the SAFE surface. */
export const useSalesforceSession = (): boolean => {
  const sfToken = useSelector((state: RootState) => state.user.sfToken);
  const authz = useSelector((state: RootState) => state.user?.authz);

  return hasSalesforceSession(sfToken, authz);
};

export default useSalesforceSession;
